/**
 * Minimal history-based React router
 * Uses clean URLs: /login, /dashboard, etc.
 */

import { useState, useEffect, createContext, useContext, type MouseEvent, type ReactNode } from "react";
import { trackEvent, trackPageView } from "./apteva";
import { campaignSearch } from "./campaign";

const RouterContext = createContext<string>("/");
const APP_ROUTES = new Set(["/login", "/register"]);

function normalizePath(path: string) {
  const [base] = path.split("?");
  if (base === "/") return "/";
  return base.replace(/\/+$/, "");
}

export function appUrl(path: string, campaign = true) {
  const base = (typeof window !== "undefined" && (window as any).__APP_URL__) || "https://app.socialcast.dev";
  const url = new URL(path, base);
  if (campaign) new URLSearchParams(campaignSearch()).forEach((value, key) => url.searchParams.set(key, value));
  return url.toString();
}

function isExternalHref(href: string) {
  return /^(https?:|mailto:|tel:|#)/.test(href);
}

export function Router({ children, initialPath }: { children: ReactNode; initialPath?: string }) {
  const [path, setPath] = useState(() => {
    if (initialPath) return initialPath;
    if (typeof window !== "undefined") return window.location.pathname;
    return "/";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => {
      setPath(window.location.pathname);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    trackPageView(path);
  }, [path]);

  return <RouterContext.Provider value={path}>{children}</RouterContext.Provider>;
}

export function Route({ path, component: Component }: { path: string; component: () => ReactNode }) {
  const currentPath = useContext(RouterContext);
  const currentBase = normalizePath(currentPath);
  if (currentBase !== normalizePath(path)) return null;
  return <Component />;
}

export function Link({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const label = event.currentTarget.textContent?.trim() || href;
    trackEvent("socialcast_site_link_click", {
      href,
      label,
      source_path: window.location.pathname,
      target: APP_ROUTES.has(href) ? "app" : isExternalHref(href) ? "external" : "site",
    });
    onClick?.();

    if (isExternalHref(href)) return;

    event.preventDefault();
    if (APP_ROUTES.has(href)) {
      window.location.href = appUrl(href);
      return;
    }

    navigate(href);
  };

  return (
    <a
      href={APP_ROUTES.has(href) ? appUrl(href, false) : href}
      className={className}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}

export function navigate(path: string) {
  campaignSearch();
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useRoute(): string {
  return useContext(RouterContext);
}

export function useSearchParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}
